import { apiFetch, httpError } from '../services/apiFetch';

const API_URL = import.meta.env.VITE_API_URL;

// Sends an org invitation via our API rather than the Clerk JS SDK.
// We have to do this server-side because organization.inviteMembers() in
// the JS SDK silently drops the publicMetadata parameter — fatal for
// Worker invites since we encode Worker as org:member + workerMode flag.
// The Backend API (which our server proxies) DOES honour public_metadata.
//
// role: 'Admin' | 'Manager' | 'Worker' (NOT the Clerk role string).
export async function inviteMember(email, role) {
    const res = await apiFetch(`${API_URL}/api/Members/Invite`, {
        method: 'POST',
        body: JSON.stringify({ email, role }),
    });
    if (!res.ok) throw await httpError(res, 'Failed to send invitation');
    return true;
}

// Pre-flight check before invoking the invite endpoint.
// Catches the multi-org Admin/Manager rule so the Admin sees a friendly
// form-validation error instead of the invite silently being revoked
// after the recipient accepts.
//
// role: 'Admin' | 'Member' | 'Worker' — the MarginPal role string, NOT
// the Clerk role. The caller maps from the role-picker selection.
//
// Returns { allowed: bool, reason?: string }.
export async function checkInviteEligibility(email, role) {
    const res = await apiFetch(`${API_URL}/api/Members/CheckInviteEligibility`, {
        method: 'POST',
        body: JSON.stringify({ email, role }),
    });
    if (!res.ok) throw await httpError(res, 'Failed to check invite eligibility');
    return res.json();
}

// Lightweight list of every active member in the tenant, with our internal
// User.Id (which JobAssignmentController.Assign takes). Used by the
// JobAssignments picker on the job detail page so we don't have to map
// from Clerk's user IDs at assign-time.
export async function fetchActiveMembers() {
    const res = await apiFetch(`${API_URL}/api/Members/GetActiveMembers`, { method: 'GET' });
    if (!res.ok) throw await httpError(res, 'Failed to fetch members');
    return res.json();
}

// Updates an existing member's role. Updates BOTH Clerk (so the JWT carries
// the new role on next refresh) and our DB (so policy checks reflect the
// change immediately). Throws if the multi-org rule blocks it.
//
// userId: our internal User.Id (not the Clerk user id). Caller resolves
// it from the membership row's CurrentUserId / our user table.
// newRole: 'Admin' | 'Member' | 'Worker'.
export async function updateMemberRole(userId, newRole) {
    const res = await apiFetch(`${API_URL}/api/Members/UpdateRole`, {
        method: 'POST',
        body: JSON.stringify({ userId, newRole }),
    });
    if (!res.ok) throw await httpError(res, 'Failed to update role');
    return true;
}
